import type {
  CasaLeonOperationalEvent,
  CasaLeonWorldSnapshot,
} from '@casa-leon-vivo/contracts/casa-leon';
import { placeOrUpdateTable, releaseTable } from './table-layout.js';
import { staffPresenceRule } from './staff-rules.js';

export function emptyCasaLeonSnapshot(): CasaLeonWorldSnapshot {
  return {
    simulated: true,
    businessId: 'casa-leon',
    revision: 0,
    appliedEventIds: [],
    tables: [],
    staff: [],
    sectorPulses: [],
    register: { open: true, salesToday: 0 },
  };
}

export function applyOperationalEvent(
  snapshot: CasaLeonWorldSnapshot,
  event: CasaLeonOperationalEvent,
): CasaLeonWorldSnapshot {
  if (snapshot.appliedEventIds.includes(event.id)) {
    return snapshot;
  }

  let next: CasaLeonWorldSnapshot = {
    ...snapshot,
    revision: snapshot.revision + 1,
    appliedEventIds: [...snapshot.appliedEventIds, event.id],
  };

  switch (event.type) {
    case 'table.session.open': {
      const placed = placeOrUpdateTable(next.tables, {
        tableId: event.payload.tableId,
        sector: event.payload.sector,
        partySize: event.payload.partySize,
      });
      if (!placed.error) {
        next = {
          ...next,
          tables: placed.tables.map((t) =>
            t.tableId === event.payload.tableId
              ? {
                  ...t,
                  staffId: event.payload.staffId,
                  consumptionTotal: event.payload.consumptionTotal,
                  operationalState: 'abierta',
                }
              : t,
          ),
        };
      }
      break;
    }
    case 'table.session.update_party': {
      const placed = placeOrUpdateTable(next.tables, {
        tableId: event.payload.tableId,
        sector: next.tables.find((t) => t.tableId === event.payload.tableId)?.sector ?? 'salon',
        partySize: event.payload.partySize,
      });
      if (!placed.error) {
        next = { ...next, tables: placed.tables };
      }
      break;
    }
    case 'table.session.state': {
      next = {
        ...next,
        tables: next.tables.map((t) =>
          t.tableId === event.payload.tableId
            ? { ...t, operationalState: event.payload.operationalState }
            : t,
        ),
      };
      break;
    }
    case 'table.session.release':
    case 'table.session.close': {
      next = { ...next, tables: releaseTable(next.tables, event.payload.tableId) };
      break;
    }
    case 'staff.presence':
    case 'staff.role': {
      next = { ...next, staff: staffPresenceRule(next.staff, event.payload) };
      break;
    }
    case 'sector.activity': {
      next = {
        ...next,
        sectorPulses: [
          ...next.sectorPulses.filter((p) => p.sector !== event.sector),
          {
            sector: event.sector,
            activity: event.payload.activity,
            until: event.timestamp + 4000,
          },
        ],
      };
      break;
    }
    case 'delivery.lifecycle': {
      next = {
        ...next,
        delivery: { deliveryId: event.payload.deliveryId, phase: event.payload.phase },
      };
      break;
    }
    case 'payment.registered': {
      next = {
        ...next,
        register: {
          open: true,
          salesToday: (next.register?.salesToday ?? 0) + event.payload.amount,
        },
      };
      break;
    }
    case 'stock.movement': {
      const stock = [...(next.stock ?? [])];
      const idx = stock.findIndex((s) => s.sku === event.payload.sku);
      if (idx >= 0) {
        stock[idx] = { sku: event.payload.sku, qty: stock[idx].qty + event.payload.delta };
      } else {
        stock.push({ sku: event.payload.sku, qty: event.payload.delta });
      }
      next = { ...next, stock: stock.filter((s) => s.qty > 0) };
      break;
    }
    case 'supplier.arrival': {
      next = {
        ...next,
        supplierLast: { name: event.payload.supplierName, at: event.timestamp },
      };
      break;
    }
    default:
      break;
  }

  return next;
}

export function rebuildFromEvents(events: CasaLeonOperationalEvent[]): CasaLeonWorldSnapshot {
  let snap = emptyCasaLeonSnapshot();
  const sorted = [...events].sort((a, b) => a.timestamp - b.timestamp);
  for (const evt of sorted) {
    snap = applyOperationalEvent(snap, evt);
  }
  return snap;
}
